/**
 * Font Helper Functions
 * 
 * This module provides utilities for loading and managing web fonts
 * from the Canou Fonts API.
 */

/**
 * Loads a font family from a font object or direct URL
 * 
 * @param {Object|string} fontData - Font object or font family name
 * @param {function} logger - Optional logging function
 * @returns {Promise<boolean>} - Promise resolving to true if font loaded successfully
 */
export const loadFont = async (fontData, logger = console.log) => {
	// Skip loading if no data
	if (!fontData) {
		return false;
	}

	// Determine if we have an object format
	const isObjectFormat = typeof fontData === 'object' && fontData !== null;
	// If fontData contains a related font object, use it; otherwise use fontData itself
	const actualFont = isObjectFormat && fontData.font ? fontData.font : fontData;
	
	// Extract necessary data from actualFont
	const fontFamily = actualFont.title;
	const fontUrl = actualFont.url || null;
	const fontStyle = actualFont.style || 'normal';
	const fontWeight = actualFont.weight || 400;
    
	// Skip system fonts
	if (!fontFamily || 
		fontFamily === 'inherit' || 
		fontFamily === 'system-ui' || 
		fontFamily === 'sans-serif' || 
		fontFamily === 'serif' || 
		fontFamily === 'monospace') {
		return false;
	}
    
	try {
		// Normalize font name for tracking purposes
		const normalizedFontName = fontFamily.toLowerCase().replace(/\s+/g, '');
		
		// Check if this font is already loading or loaded
		const fontLoaderAttr = `data-font-${normalizedFontName}`;
		if (document.querySelector(`[${fontLoaderAttr}]`)) {
			logger(`Font ${fontFamily} already loaded or loading`);
			return true;
		}
		
		logger(`Loading font: ${fontFamily}${isObjectFormat ? ' from direct URL' : ' from fonts.canou.io'}`);
		
		// Create a style element with high priority
		const styleEl = document.createElement('style');
		styleEl.setAttribute(fontLoaderAttr, 'loading');
		styleEl.setAttribute('data-priority', 'high');
		
		// Insert at beginning of head for higher CSS precedence
		const head = document.head;
		head.insertBefore(styleEl, head.firstChild);
		
		let cssText;
		
		if (fontUrl) {
			// Direct URL loading from object
			cssText = `
@font-face {
	font-family: '${fontFamily}';
	font-style: ${fontStyle};
	font-weight: ${fontWeight};
	font-display: swap;
	src: url('${fontUrl}') format('woff2');
}`;
			logger(`Created @font-face rule for ${fontFamily} from direct URL`);
		} else {
			// Legacy URL construction method
			const response = await fetch(`https://fonts.canou.io/?family=${normalizedFontName}`, {
				credentials: 'same-origin',
				mode: 'cors',
				headers: {
					'Accept': 'text/css,*/*;q=0.1'
				}
			});
			
			if (!response.ok) {
				throw new Error(`Failed to load font: ${response.status} ${response.statusText}`);
			}
			
			cssText = await response.text();
			
			// Check if we got valid font-face content
			if (!cssText.includes('@font-face')) {
				throw new Error(`Invalid font definition received for ${fontFamily}`);
			}
		}
		
		// Add debugging helpers in dev mode
		const fontDebugCss = process.env.NODE_ENV === 'development' ? `
/* Font debugging helper */
[data-font-debug="${normalizedFontName}"] {
	font-family: "${fontFamily}", sans-serif !important;
	border-bottom: 2px solid green;
}` : '';
		
		// Update the style element with the font definitions
		styleEl.textContent = cssText + fontDebugCss;
		styleEl.setAttribute(fontLoaderAttr, 'loaded');
		
		// Create a test utility function in window object for checking font
		if (process.env.NODE_ENV === 'development') {
			window.testFont = window.testFont || {};
			window.testFont[normalizedFontName] = (selector = 'body') => {
				const el = document.querySelector(selector);
				if (el) {
					el.setAttribute('data-font-debug', normalizedFontName);
					return `Applied ${fontFamily} to ${selector} for testing`;
				}
				return `Element not found: ${selector}`;
			};
		}
		
		logger(`Font loaded successfully: ${fontFamily}`);
		return true;
	} catch (error) {
		logger(`Error loading font ${fontFamily}:`, error);
		return false;
	}
};

/**
 * Loads multiple font families at once
 * 
 * @param {Array} fontDataArray - Array of font objects or font family names
 * @param {function} logger - Optional logging function
 * @returns {Promise<boolean[]>} - Promise resolving to array of load results
 */
export const loadFonts = async (fontDataArray = [], logger = console.log) => {
	if (!Array.isArray(fontDataArray) || fontDataArray.length === 0) {
		return [];
	}
	
	logger(`Starting to load ${fontDataArray.length} fonts`);
	
	// Load all fonts in parallel
	const loadPromises = fontDataArray
		.filter(Boolean) // Filter out null/undefined entries
		.map(fontData => loadFont(fontData, logger));
	
	const results = await Promise.all(loadPromises);
	
	const successCount = results.filter(Boolean).length;
	logger(`Fonts loaded: ${successCount}/${fontDataArray.length}`);
	
	return results;
};

/**
 * Checks if a font has already been loaded
 * 
 * @param {Object|string} fontData - Font object or font family name
 * @returns {boolean} - True if font is already loaded
 */
export const isFontLoaded = (fontData) => {
	if (!fontData) return false;
	const actualFont = typeof fontData === 'object' && fontData.font ? fontData.font : fontData;
	const fontFamily = actualFont.title;
	if (!fontFamily) return false;
	const normalizedFontName = fontFamily.toLowerCase().replace(/\s+/g, '');
	const fontLoaderAttr = `data-font-${normalizedFontName}`;
	const fontEl = document.querySelector(`[${fontLoaderAttr}="loaded"]`);
	return !!fontEl;
};

/**
 * Adds a font test element to the document that shows if the font is actually rendering
 * Only used in development mode
 * 
 * @param {Object|string} fontData - Font object or font family name
 */
export const testFontRendering = (fontData) => {
	if (process.env.NODE_ENV !== 'development' || !fontData) return;
	const actualFont = typeof fontData === 'object' && fontData.font ? fontData.font : fontData;
	const fontFamily = actualFont.title;
	if (!fontFamily) return;
	let testEl = document.getElementById('font-test-panel');
	if (!testEl) {
		testEl = document.createElement('div');
		testEl.id = 'font-test-panel';
		testEl.style.cssText = 'position:fixed;bottom:10px;right:10px;background:#fff;border:1px solid #ccc;padding:10px;z-index:10000;max-width:300px;font-size:12px;';
		document.body.appendChild(testEl);
	}
	const fontSample = document.createElement('p');
	fontSample.style.fontFamily = `"${fontFamily}", sans-serif`;
	fontSample.style.margin = '5px 0';
	fontSample.style.padding = '5px';
	fontSample.style.border = '1px solid #eee';
	fontSample.textContent = `Text in ${fontFamily}`;
	testEl.appendChild(fontSample);
};
